<template>
  <nav
    class="bg-blue-900 shadow-sm"
    v-if="
      [
        '/results/all',
        '/referrals/all',
        '/stats',
        '/account/reconciler',
        '/results/checks',
        '/programs/checks',
      ].includes(route.path)
    "
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Logo/Brand -->
        <div class="flex-shrink-0 flex items-center">
          <h1 class="text-xl font-bold text-white">Admin Dashboard</h1>
        </div>

        <!-- Mobile menu button -->
        <div class="flex items-center sm:hidden">
          <button
            @click="isMobileMenuOpen = !isMobileMenuOpen"
            class="inline-flex items-center justify-center p-2 rounded-md text-blue-100 hover:text-white hover:bg-blue-800 focus:outline-none"
          >
            <svg
              class="h-6 w-6"
              :class="{ hidden: isMobileMenuOpen, block: !isMobileMenuOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
            <svg
              class="h-6 w-6"
              :class="{ block: isMobileMenuOpen, hidden: !isMobileMenuOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Desktop Navigation -->
        <div class="hidden sm:flex sm:items-center sm:space-x-6">
          <NuxtLink
            to="/stats"
            class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
            :class="[
              $route.path === '/stats'
                ? 'border-blue-300 text-blue-200'
                : 'border-transparent text-blue-100 hover:border-blue-200 hover:text-white',
            ]"
          >
            Stats
          </NuxtLink>
          <NuxtLink
            to="/referrals/all"
            class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
            :class="[
              $route.path === '/referrals/all'
                ? 'border-blue-300 text-blue-200'
                : 'border-transparent text-blue-100 hover:border-blue-200 hover:text-white',
            ]"
          >
            Referrals
          </NuxtLink>
          <NuxtLink
            to="/programs/checks"
            class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
            :class="[
              $route.path === '/programs/checks'
                ? 'border-blue-300 text-blue-200'
                : 'border-transparent text-blue-100 hover:border-blue-200 hover:text-white',
            ]"
          >
            Programs Checks
          </NuxtLink>
          <NuxtLink
            to="/account/reconciler"
            class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
            :class="[
              $route.path === '/account/reconciler'
                ? 'border-blue-300 text-blue-200'
                : 'border-transparent text-blue-100 hover:border-blue-200 hover:text-white',
            ]"
          >
            Account Reconciler
          </NuxtLink>

          <!-- Results Dropdown -->
          <div class="relative inline-flex">
            <button
              @click.stop="toggleDropdown"
              class="inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors duration-200"
              :class="{
                'border-blue-300 text-blue-200': isResultsActive,
                'border-transparent text-blue-100 hover:border-blue-200 hover:text-white':
                  !isResultsActive,
              }"
            >
              Results
              <svg
                class="w-4 h-4 ml-1 transition-transform duration-200"
                :class="{
                  'rotate-180': showResultsDropdown,
                  'text-blue-200': isResultsActive,
                  'text-blue-100': !isResultsActive,
                }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            <transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 translate-y-1"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-1"
            >
              <div
                v-show="showResultsDropdown"
                class="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-blue-800 border border-blue-700 z-50"
                @click.stop
              >
                <div class="py-1">
                  <NuxtLink
                    to="/results/checks"
                    class="block px-4 py-2 text-sm text-blue-100 hover:bg-blue-700"
                    @click="closeDropdown"
                  >
                    Checks
                  </NuxtLink>
                  <NuxtLink
                    to="/results/all"
                    class="block px-4 py-2 text-sm text-blue-100 hover:bg-blue-700"
                    @click="closeDropdown"
                  >
                    All Results
                  </NuxtLink>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <transition
      enter-active-class="transition-opacity ease-linear duration-300"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity ease-linear duration-300"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-show="isMobileMenuOpen" class="sm:hidden">
        <div class="pt-2 pb-3 space-y-1">
          <NuxtLink
            to="/stats"
            class="block pl-3 pr-4 py-2 text-base font-medium transition-colors duration-200"
            :class="[
              $route.path === '/stats'
                ? 'bg-blue-800 text-blue-200'
                : 'text-blue-100 hover:bg-blue-800 hover:text-white',
            ]"
            @click="isMobileMenuOpen = false"
          >
            Stats
          </NuxtLink>
          <NuxtLink
            to="/account/reconciler"
            class="block pl-3 pr-4 py-2 text-base font-medium transition-colors duration-200"
            :class="[
              $route.path === '/account/reconciler'
                ? 'bg-blue-800 text-blue-200'
                : 'text-blue-100 hover:bg-blue-800 hover:text-white',
            ]"
            @click="isMobileMenuOpen = false"
          >
            Account Reconciler
          </NuxtLink>
          <!-- Mobile Results Menu -->
          <div>
            <button
              @click="mobileResultsOpen = !mobileResultsOpen"
              class="w-full flex items-center justify-between pl-3 pr-4 py-2 text-base font-medium text-blue-100 hover:bg-blue-800 hover:text-white"
            >
              Results
              <svg
                class="w-4 h-4 ml-1 transition-transform duration-200"
                :class="{ 'rotate-180': mobileResultsOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div v-show="mobileResultsOpen" class="pl-4">
              <NuxtLink
                to="/results/checks"
                class="block pl-3 pr-4 py-2 text-sm font-medium text-blue-100 hover:bg-blue-800 hover:text-white"
                @click="closeAllMenus"
              >
                Checks
              </NuxtLink>
              <NuxtLink
                to="/results/all"
                class="block pl-3 pr-4 py-2 text-sm font-medium text-blue-100 hover:bg-blue-800 hover:text-white"
                @click="closeAllMenus"
              >
                All Results
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
const route = useRoute();

const showResultsDropdown = ref(false);
const isMobileMenuOpen = ref(false);
const mobileResultsOpen = ref(false);

const isResultsActive = computed(() => {
  return route.path.startsWith("/results");
});

const toggleDropdown = () => {
  showResultsDropdown.value = !showResultsDropdown.value;
};

const closeDropdown = () => {
  showResultsDropdown.value = false;
};

const closeAllMenus = () => {
  isMobileMenuOpen.value = false;
  mobileResultsOpen.value = false;
  showResultsDropdown.value = false;
};

// Close dropdown when clicking outside or navigating
watch(
  () => route.path,
  () => {
    closeAllMenus();
  }
);

const clickHandler = (event) => {
  const dropdown = document.querySelector(".relative.inline-flex");
  if (dropdown && !dropdown.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener("click", clickHandler);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", clickHandler);
});
</script>
